import React, { useContext } from "react";
import {
  DefaultMantineColor,
  Navbar as MantineNavbar,
  Tabs,
  useMantineTheme,
  Text,
  Accordion,
} from "@mantine/core";
import {
  FaHome,
  FaUser,
  FaUserShield,
  FaMapMarkerAlt,
  FaServicestack,
  FaProductHunt,
} from "react-icons/fa";
import { AiFillClockCircle } from "react-icons/ai";
import { MdDiscount, MdMeetingRoom } from "react-icons/md";
import { TbHierarchy } from "react-icons/tb";
import { AppContext } from "src/api/AppContext";
import { useStyles } from "./styles";
import { RolePermissionType, UserRole } from "src/generated/graphql";
import { TAB_LIST } from "src/utils/constants";
import { RiCalendar2Fill, RiFilePaper2Line } from "react-icons/ri";
import {
  BiSolidMessageSquareDetail,
  BiSolidDashboard,
  BiRevision,
} from "react-icons/bi";

interface NavbarProps {
  isOpened: boolean;
  setNavbarOpened: (opened: boolean) => void;
}

enum TabSectionType {
  General = "General",
  Organization = "Organziation",
  Management = "Management",
  ProfileMenu = "ProfileMenu",
}

type TabType = {
  key: string;
  label: string;
  roles?: UserRole[];
  permission?: RolePermissionType;
  icon?: React.ReactNode;
  color?: DefaultMantineColor;
  elementOverride?: React.ReactNode;
  section?: TabSectionType;
};

type TabSection = {
  type: TabSectionType;
  label: string;
  screens: TabType[];
};

const Navbar: React.FC<NavbarProps> = ({
  isOpened,
  setNavbarOpened,
}: NavbarProps) => {
  const {
    changeTab,
    activeTabKey,
    currentUser,
    currentEmployee,
    isDarkMode,
    currentLocation,
  } = useContext(AppContext);
  const theme = useMantineTheme();
  const { classes } = useStyles();

  const NavigationList: TabSection[] = [
    {
      type: TabSectionType.General,
      label: "General",
      screens: [
        {
          key: TAB_LIST.HOME,
          label: TAB_LIST.HOME,
          icon: <FaHome size={17} />,
          // roles: [UserRole.Admin],
          permission: currentEmployee?.role?.locDashboard,
          roles: [UserRole.Admin],
          section: TabSectionType.General,
        },
        {
          key: TAB_LIST.CALENDER,
          label: TAB_LIST.CALENDER,
          icon: <RiCalendar2Fill size={17} />,
          // roles: [],
          permission: currentEmployee?.role?.calendar,
          roles: [],
          section: TabSectionType.General,
        },
        {
          key: TAB_LIST.CALENDER_RECURRING_EVENTS,
          label: TAB_LIST.CALENDER_RECURRING_EVENTS,
          icon: <BiRevision size={17} />,
          permission: currentEmployee?.role?.calendar,
          roles: [],
          section: TabSectionType.General,
        },
        {
          key: TAB_LIST.CLIENT,
          label: TAB_LIST.CLIENT,
          icon: <FaUser size={17} />,
          permission: currentEmployee?.role?.clients,
          roles: [],
          section: TabSectionType.General,
        },
      ],
    },
    {
      type: TabSectionType.Management,
      label: "Location Management",
      screens: [
        {
          key: TAB_LIST.GENERAL,
          label: TAB_LIST.GENERAL,
          icon: <FaMapMarkerAlt size={17} />,
          // roles: [UserRole.Employee],
          permission: currentEmployee?.role?.locDashboard,
          roles: [UserRole.Employee],
          section: TabSectionType.Management,
        },
        {
          key: TAB_LIST.DASHBOARD,
          label: TAB_LIST.DASHBOARD,
          icon: <BiSolidDashboard size={17} />,
          permission: currentEmployee?.role?.locDashboard,
          roles: [],
          section: TabSectionType.Management,
        },
        {
          key: TAB_LIST.REPORTS,
          label: TAB_LIST.REPORTS,
          icon: <BiSolidDashboard size={17} />,
          roles: [],
          section: TabSectionType.Management,
        },
        {
          key: TAB_LIST.LOCATION_SERVICE,
          label: TAB_LIST.LOCATION_SERVICE,
          icon: <FaServicestack size={17} />,
          permission: currentEmployee?.role?.locServices,
          roles: [],
          section: TabSectionType.Management,
        },
        {
          key: TAB_LIST.EMPLOYEE,
          label: TAB_LIST.EMPLOYEE,
          icon: <FaUser size={17} />,
          permission: currentEmployee?.role?.locEmployees,
          roles: [],
          section: TabSectionType.Management,
        },
        {
          key: TAB_LIST.TIME_CLOCK,
          label: TAB_LIST.TIME_CLOCK,
          icon: <AiFillClockCircle size={17} />,
          permission: currentEmployee?.role?.locTimeclock,
          roles: [],
          section: TabSectionType.Management,
        },
        {
          key: TAB_LIST.STATIONS,
          label: TAB_LIST.STATIONS,
          icon: <AiFillClockCircle size={17} />,
          permission: currentEmployee?.role?.employeeStations,
          roles: [],
          section: TabSectionType.Management,
        },
        {
          key: TAB_LIST.ROOM,
          label: TAB_LIST.ROOM,
          icon: <MdMeetingRoom size={17} />,
          permission: currentEmployee?.role?.locRooms,
          roles: [],
          section: TabSectionType.Management,
        },
        {
          key: TAB_LIST.AGREEMENT,
          label: TAB_LIST.AGREEMENT,
          icon: <RiFilePaper2Line size={17} />,
          permission: currentEmployee?.role?.locAgreements,
          roles: [],
          section: TabSectionType.Management,
        },
        {
          key: TAB_LIST.PACKAGE,
          label: TAB_LIST.PACKAGE,
          icon: <RiFilePaper2Line size={17} />,
          permission: currentEmployee?.role?.locPackages,
          roles: [],
          section: TabSectionType.Management,
        },
        {
          key: TAB_LIST.PRODUCT,
          label: TAB_LIST.PRODUCT,
          icon: <FaProductHunt size={17} />,
          permission: currentEmployee?.role?.locProducts,
          roles: [],
          section: TabSectionType.Management,
        },
        {
          key: TAB_LIST.REQUESTS,
          label: TAB_LIST.REQUESTS,
          icon: <BiSolidMessageSquareDetail size={17} />,
          permission: currentEmployee?.role?.locRequests,
          roles: [],
          section: TabSectionType.Management,
        },
      ],
    },
    {
      type: TabSectionType.Organization,
      label: "Organization Management",
      screens: [
        {
          key: TAB_LIST.ORGANIZATION,
          label: TAB_LIST.ORGANIZATION,
          icon: <TbHierarchy size={17} />,
          permission: currentEmployee?.role?.orgSettings,
          roles: [UserRole.Admin],
          section: TabSectionType.Organization,
        },
        {
          key: TAB_LIST.ORG_GENERAL + "_Org",
          label: TAB_LIST.ORG_GENERAL,
          icon: <TbHierarchy size={17} />,
          // roles: [UserRole.Employee],
          permission: currentEmployee?.role?.orgSettings,
          roles: [UserRole.Employee],
          section: TabSectionType.Organization,
        },
        {
          key: TAB_LIST.ROLES,
          label: TAB_LIST.ROLES,
          icon: <FaUserShield size={17} />,
          permission: currentEmployee?.role?.orgRoles,
          roles: [UserRole.Admin],
          section: TabSectionType.Organization,
        },
        {
          key: TAB_LIST.POLICY,
          label: TAB_LIST.POLICY,
          icon: <FaUserShield size={17} />,
          permission: currentEmployee?.role?.orgPolicies,
          roles: [],
          section: TabSectionType.Organization,
        },
        {
          key: TAB_LIST.LOCATION,
          label: TAB_LIST.LOCATION,
          icon: <FaMapMarkerAlt size={17} />,
          permission: currentEmployee?.role?.orgLocations,
          roles: [],
          section: TabSectionType.Organization,
        },
        {
          key: TAB_LIST.SERVICE,
          label: TAB_LIST.SERVICE,
          icon: <FaServicestack size={17} />,
          permission: currentEmployee?.role?.orgService,
          roles: [],
          section: TabSectionType.Organization,
        },
        {
          key: TAB_LIST.DISCOUNTS,
          label: TAB_LIST.DISCOUNTS,
          icon: <MdDiscount size={17} />,
          permission: currentEmployee?.role?.orgDiscounts,
          roles: [],
          section: TabSectionType.Organization,
        },
        {
          key: TAB_LIST.ORG_AGREEMENT,
          label: TAB_LIST.ORG_AGREEMENT,
          icon: <RiFilePaper2Line size={17} />,
          permission: currentEmployee?.role?.orgAgreements,
          roles: [],
          section: TabSectionType.Organization,
        },
        {
          key: TAB_LIST.ORG_PACKAGE,
          label: TAB_LIST.ORG_PACKAGE,
          icon: <RiFilePaper2Line size={17} />,
          permission: currentEmployee?.role?.orgPackages,
          roles: [],
          section: TabSectionType.Organization,
        },
      ],
    },
  ];

  const Tablist: TabType[] = [
    {
      key: TAB_LIST.DASHBOARD,
      label: TAB_LIST.DASHBOARD,
      icon: <BiSolidDashboard size={17} />,
      roles: [],
      section: TabSectionType.Management,
    },
    {
      key: TAB_LIST.HOME,
      label: TAB_LIST.HOME,
      icon: <FaHome size={17} />,
      roles: [UserRole.Admin],
      section: TabSectionType.General,
    },
    {
      key: TAB_LIST.CALENDER,
      label: TAB_LIST.CALENDER,
      icon: <RiCalendar2Fill size={17} />,
      roles: [],
      section: TabSectionType.General,
    },
    {
      key: TAB_LIST.CLIENT,
      label: TAB_LIST.CLIENT,
      icon: <FaUser size={17} />,
      roles: [],
      section: TabSectionType.General,
    },
    {
      key: TAB_LIST.ORGANIZATION,
      label: TAB_LIST.ORGANIZATION,
      icon: <TbHierarchy size={17} />,
      roles: [],
      section: TabSectionType.Organization,
    },
    {
      key: TAB_LIST.POLICY,
      label: TAB_LIST.POLICY,
      icon: <FaUserShield size={17} />,
      roles: [],
      section: TabSectionType.Organization,
    },
    {
      key: TAB_LIST.LOCATION,
      label: TAB_LIST.LOCATION,
      icon: <FaMapMarkerAlt size={17} />,
      roles: [],
      section: TabSectionType.Organization,
    },
    {
      key: TAB_LIST.ORG_AGREEMENT,
      label: TAB_LIST.ORG_AGREEMENT,
      icon: <RiFilePaper2Line size={17} />,
      roles: [],
      section: TabSectionType.Organization,
    },
    {
      key: TAB_LIST.ORG_PACKAGE,
      label: TAB_LIST.ORG_PACKAGE,
      icon: <RiFilePaper2Line size={17} />,
      roles: [],
      section: TabSectionType.Organization,
    },
    {
      key: TAB_LIST.EMPLOYEE,
      label: TAB_LIST.EMPLOYEE,
      icon: <FaUser size={17} />,
      roles: [],
      section: TabSectionType.Management,
    },
    {
      key: TAB_LIST.TIME_CLOCK,
      label: TAB_LIST.TIME_CLOCK,
      icon: <AiFillClockCircle size={17} />,
      roles: [],
      section: TabSectionType.Management,
    },
    {
      key: TAB_LIST.STATIONS,
      label: TAB_LIST.STATIONS,
      icon: <AiFillClockCircle size={17} />,
      roles: [],
      section: TabSectionType.Management,
    },
    {
      key: TAB_LIST.SERVICE,
      label: TAB_LIST.SERVICE,
      icon: <FaServicestack size={17} />,
      roles: [],
      section: TabSectionType.Organization,
    },
    {
      key: TAB_LIST.ROOM,
      label: TAB_LIST.ROOM,
      icon: <MdMeetingRoom size={17} />,
      roles: [],
      section: TabSectionType.Management,
    },
    {
      key: TAB_LIST.AGREEMENT,
      label: TAB_LIST.AGREEMENT,
      icon: <RiFilePaper2Line size={17} />,
      roles: [],
      section: TabSectionType.Management,
    },
    {
      key: TAB_LIST.PACKAGE,
      label: TAB_LIST.PACKAGE,
      icon: <RiFilePaper2Line size={17} />,
      roles: [],
      section: TabSectionType.Management,
    },
    {
      key: TAB_LIST.PRODUCT,
      label: TAB_LIST.PRODUCT,
      icon: <FaProductHunt size={17} />,
      roles: [],
      section: TabSectionType.Management,
    },
    {
      key: TAB_LIST.DISCOUNTS,
      label: TAB_LIST.DISCOUNTS,
      icon: <MdDiscount size={17} />,
      roles: [],
      section: TabSectionType.Organization,
    },
    {
      key: TAB_LIST.REQUESTS,
      label: TAB_LIST.REQUESTS,
      icon: <FaProductHunt size={17} />,
      roles: [],
      section: TabSectionType.Management,
    },
  ];

  let activeTab = Tablist.find((tab) => tab.key === activeTabKey);

  return (
    <MantineNavbar
      p="md"
      className={classes.navbar}
      width={{ sm: 220, md: 220, lg: 220, base: 220 }}
      style={{
        borderRight: "none",
        marginLeft: !isOpened ? -220 : 0,
        transition: "margin-left 0.3s",
        backgroundColor: "#00000000",
        zIndex: 98,
      }}
    >
      <div
        style={{
          display: "flex",
          height: "100%",
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            height: "100%",
            display: "flex",
            flex: 1,
            backgroundColor: theme.colors.secondary[6],
          }}
        >
          <MantineNavbar.Section>
            <Tabs
              orientation="vertical"
              value={activeTabKey}
              onTabChange={(key) => {
                changeTab(key || "");
                setNavbarOpened(false);
              }}
              sx={{
                width: 200,
                borderRadius: 4,
                overflow: "hidden",
              }}
            >
              <Tabs.List style={{ borderRight: "none" }} grow>
                <Accordion
                  defaultValue={activeTab?.section}
                  sx={{
                    ".mantine-Accordion-item:last-of-type": {
                      borderBottom: 0,
                    },
                  }}
                >
                  {NavigationList.map((navigationSection, index) => {
                    return (
                      <Accordion.Item
                        key={navigationSection.label + index}
                        value={navigationSection.type}
                      >
                        <Accordion.Control
                          style={{
                            color: theme.colors.secondaryTextColor[6],
                            backgroundColor: theme.colors.secondary[5],
                          }}
                          sx={{
                            "&:hover": {
                              backgroundColor: theme.colors.secondary[3],
                            },
                          }}
                        >
                          {navigationSection.label}
                        </Accordion.Control>
                        <Accordion.Panel
                          sx={{
                            width: 200,
                            ".mantine-Accordion-content": {
                              padding: "0px !important",
                              display: "flex",
                              flexDirection: "column",
                              width: 200,
                            },
                          }}
                        >
                          {navigationSection.screens.map((tab) => {
                            let permitted = true; //false;
                            if (
                              tab.key === TAB_LIST.STATIONS &&
                              !currentLocation?.stationsEnabled
                            ) {
                              return;
                            }
                            tab.roles?.forEach((userRole) => {
                              if (currentUser?.role === userRole) {
                                permitted = true;
                              }
                            });

                            if (
                              (tab.roles && !permitted) ||
                              tab.permission == RolePermissionType.None
                            ) {
                              return undefined;
                            } else if (tab.elementOverride) {
                              return tab.elementOverride;
                            } else
                              return (
                                <Tabs.Tab
                                  key={tab.key}
                                  value={tab.key}
                                  icon={tab.icon}
                                  color={tab.color}
                                  sx={{
                                    backgroundColor: theme.colors.secondary[2],
                                    "&:hover": {
                                      backgroundColor:
                                        theme.colors.secondary[3],
                                    },
                                  }}
                                  style={{
                                    ...{
                                      padding: 5,
                                      paddingLeft: 10,
                                      borderRadius: 0,
                                      borderWidth: 0,
                                      borderRight: "none !important",
                                      color: theme.colors.secondaryTextColor[0],
                                      flex: 1,
                                      marginRight: 0,
                                    },
                                    ...(activeTabKey === tab.key
                                      ? {
                                          backgroundColor:
                                            theme.colors.primary[6],
                                          fontWeight: 500,
                                          color:
                                            theme.colors.primaryTextColor[6],
                                        }
                                      : {}),
                                  }}
                                >
                                  <Text size={18}>{tab.label}</Text>
                                </Tabs.Tab>
                              );
                          })}
                        </Accordion.Panel>
                      </Accordion.Item>
                    );
                  })}
                </Accordion>
              </Tabs.List>
            </Tabs>
          </MantineNavbar.Section>
        </div>
        <div
          style={{
            height: "100%",
            display: "flex",
            justifyContent: "space-between",
            flexDirection: "column",
          }}
        >
          <svg width={16} height={16} viewBox="0 0 16 16">
            <g fill={theme.colors.secondary[6]}>
              <path d="M 0 0 L 16 0 A 16 16 0 0 0 0 16 z" />
            </g>
          </svg>
          <svg width={16} height={16} viewBox="0 0 16 16">
            <g fill={theme.colors.secondary[6]}>
              <path d="M 0 0 A 16 16 0 0 0 16 16 L 0 16 z" />
            </g>
          </svg>
        </div>
      </div>
    </MantineNavbar>
  );
};

export default Navbar;
